import React, { useState, useMemo, JSX } from "react";
import "./UserDetails.css";
import { PlusCircleIcon } from "../../../../icons/reactIcons";
import StepIcon from "../../../../icons/svg-cons/StepIcon";
import Active24Icon from "../../../../icons/svg-cons/Active24Icon";
import NutritionIcon from "../../../../icons/svg-cons/NutritionIcon";
import SleepBedIcon from "../../../../icons/svg-cons/SleepBedIcon";
import MindFullIcon from "../../../../icons/svg-cons/MindFullIcon";
import ActivityModal from "../user-modal/ActivityModal";
import { ActivityData } from "../../../../apis/userApis";
import {
  addUserActivity,
  updateUserActivity,
} from "../../../../apis/userActivityApis";
import WaterGlassIcon from "../../../../icons/svg-cons/WaterGlassIcon";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../redux/store/store";
import { fetchUserDetails } from "../../../../redux/slices/userListSlice";
import { getCurrentYear } from "../../../../utils/getCurrentYear";

/* ================= Props ================= */

interface UserActivityProps {
  activityData: ActivityData[];
  userId: string;
  weekId?: string;
}

/* ================= Icon Mapper ================= */

const iconMap: Record<string, JSX.Element> = {
  "steps.svg": <StepIcon />,
  "activeminutes.svg": <Active24Icon />,
  "nutrition.svg": <NutritionIcon />,
  "sleep.svg": <SleepBedIcon />,
  "water.svg": <WaterGlassIcon />,
  "mindfulness.svg": <MindFullIcon />,
};

const getActivityIcon = (logoName: string) => {
  return iconMap[logoName?.toLowerCase()] || <StepIcon />;
};

/* ================= Helper ================= */

// 👇 Extract max number from string like "5000 - 10000"
const getMaxValue = (value: any): number => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const numbers = value.match(/\d+/g)?.map(Number);
    if (!numbers || numbers.length === 0) return 0;
    return Math.max(...numbers);
  }

  return 0;
};

const getActivityValueForApi = (value: string) => {
  const range = value.match(/\d+\s*-\s*\d+/);

  if (range) {
    return range[0].replace(/\s*-\s*/, " - ");
  }

  return value.match(/\d+/)?.[0] || value;
};

const getActivityDisplayValue = (activity: ActivityData) => {
  if (
    activity.activityValues === null ||
    activity.activityValues === undefined ||
    activity.activityValues === ""
  ) {
    return "";
  }

  const value = String(activity.activityValues);

  if (/[a-z]/i.test(value)) {
    return value;
  }

  const activityKey =
    `${activity.activityLogoName} ${activity.activityName}`.toLowerCase();

  if (
    activityKey.includes("activeminutes") ||
    activityKey.includes("active minutes") ||
    activityKey.includes("mindfulness")
  ) {
    return `${value} minutes`;
  }

  if (activityKey.includes("sleep")) {
    return `${value} hours`;
  }

  if (activityKey.includes("water")) {
    return `${value} Glasses`;
  }

  return value;
};

const UserActivity: React.FC<UserActivityProps> = ({
  activityData,
  userId,
  weekId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityData | null>(
    null,
  );
  const [selectedOption, setSelectedOption] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= Derived Activity UI Data ================= */

  const processedActivities = useMemo(() => {
    return (
      activityData
        ?.slice()
        ?.sort(
          (a, b) => (a.activitySortOrder ?? 0) - (b.activitySortOrder ?? 0),
        )
        ?.map((activity) => {
          const isEmpty =
            activity.activityValues === null ||
            activity.activityValues === "" ||
            activity.activityValues === 0;

          let percentage = 0;

          // 👇 Nutrition special case
          if (
            activity.activityLogoName?.toLowerCase() === "nutrition.svg" &&
            !isEmpty
          ) {
            percentage = 100;
          } else {
            const value = getMaxValue(activity.activityValues);
            const goal = Number(activity.goal) || 0;

            if (goal > 0) {
              percentage = Math.round((value / goal) * 100);
            }
          }

          let status = "";

          if (!isEmpty) {
            status =
              percentage >= 100
                ? "Achieved"
                : percentage >= 70
                  ? "In Progress"
                  : "Needs Attention";
          }

          return {
            ...activity,
            percentage,
            status,
          };
        }) || []
    );
  }, [activityData]);

  const completedCount = processedActivities.filter(
    (a) => a.status === "Achieved",
  ).length;

  const openModal = (activity: ActivityData) => {
    setSelectedActivity(activity);

    // 👇 Pre-select existing value in modal
    const activityValue =
      activity.activityValues !== null && activity.activityValues !== undefined
        ? String(activity.activityValues)
        : "";

    const matchedOption = activity.activityOption?.find(
      (option) =>
        option === activityValue ||
        getActivityValueForApi(option) ===
          getActivityValueForApi(activityValue),
    );

    setSelectedOption(matchedOption || activityValue);

    setModalOpen(true);
  };

  /* ================= API INTEGRATION ================= */

  const handleUpdate = async () => {
    if (!selectedActivity || !selectedOption) return;

    try {
      setLoading(true);
      const activityDate = new Date().toISOString().split("T")[0];
      const isEmpty =
        selectedActivity.activityMappingId === null ||
        selectedActivity.activityMappingId === "";
      const activityValues = getActivityValueForApi(selectedOption);

      if (isEmpty) {
        await addUserActivity(userId, {
          activityId: selectedActivity.activityId,
          activityValues,
          activityDate,
        });
      } else {
        await updateUserActivity(userId, {
          activityMappingId: selectedActivity.activityMappingId,
          activityValues,
          activityDate,
        });
      }

      const today = new Date().toISOString().split("T")[0];

      dispatch(
        fetchUserDetails({
          userId: userId,
          startDate: today,
          endDate: today,
          ...(weekId && { weekId: weekId }),
        }),
      );

      setModalOpen(false);
      setSelectedActivity(null);
      setSelectedOption("");
    } catch (error) {
      console.error("Activity update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="activity-container">
      <div className="activity-header">
        <h2>User Activity Progress</h2>
        <div className="activity-controls">
          <span className="completed">
            {completedCount}/{processedActivities.length} Completed
          </span>
        </div>
      </div>

      <div className="activity-grid">
        {processedActivities?.map((activity) => (
          <div
            key={activity.activityId}
            className={`activity-card ${activity.activityName}`}
          >
            <div className="card-header">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div className="card-header-icon">
                  {getActivityIcon(activity.activityLogoName)}
                </div>
                <div style={{ marginTop: "4px", textTransform: "capitalize" }}>
                  <h3>{activity.activityName}</h3>
                  <p className="desc">{activity.activityDescription}</p>
                </div>
              </div>

              <PlusCircleIcon
                fontSize={"25px"}
                style={{ cursor: "pointer" }}
                onClick={() => openModal(activity)}
              />
            </div>
            <div className="activity-progress-area">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div className="value">{getActivityDisplayValue(activity)}</div>

                <div
                  className={`status ${activity.status
                    .toLowerCase()
                    .replace(" ", "-")}`}
                >
                  {activity.status}
                </div>
              </div>
              <div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(activity.percentage, 100)}%`,
                    }}
                  ></div>
                </div>

                <div className="card-footer">
                  {Number(activity.goal) !== 0 ? (
                    <span>
                      Goal: {activity.goal} {activity.goalUnit}
                    </span>
                  ) : (
                    <span>
                      Goal:{" "}
                      {activity.goalUnit?.charAt(0).toUpperCase() +
                        activity.goalUnit?.slice(1)}
                    </span>
                  )}
                  <span>{activity.percentage}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ActivityModal
        isOpen={modalOpen}
        title={selectedActivity?.activityName || ""}
        options={selectedActivity?.activityOption || []}
        selectedOption={selectedOption}
        onSelect={setSelectedOption}
        onClose={() => setModalOpen(false)}
        onUpdate={handleUpdate}
        loading={loading}
      />
    </div>
  );
};

export default UserActivity;
